import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Scale, Phone, MessageCircle, Mail, Instagram, Facebook, Menu, X } from "lucide-react";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import { trackEvent, EVENTS } from "../lib/analytics";

const SOCIAL = {
  facebook: "https://www.facebook.com/profile.php?id=61589100911405",
  instagram: "https://www.instagram.com/fmsubastasinvestment/",
  tiktok: "https://www.tiktok.com/@fmsubastas",
};

// TikTok logo as SVG (not in lucide-react)
function TikTokIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
    </svg>
  );
}

const ICON_FM = "https://customer-assets.emergentagent.com/job_subasta-deuda-report/artifacts/x01sqrqt_Icono%202.0.png";
const LOGO_BLANCO = "https://customer-assets.emergentagent.com/job_subasta-deuda-report/artifacts/rf5tzifu_Logo%20Horizontal%20blanco.png";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#2d1f1a] border-b border-[#3d2820] h-20 flex items-center px-6" data-testid="site-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
            <img src={ICON_FM} alt="Fernando Marcos Subastas & Investment" className="h-12 w-12 object-contain flex-shrink-0" />
            <div className="hidden sm:block leading-none">
              <div className="font-display font-black text-xl lg:text-2xl tracking-tight text-white">FERNANDO MARCOS</div>
              <div className="text-[10px] lg:text-[11px] tracking-[0.3em] uppercase text-[#E0651F] font-bold mt-1">Subastas &amp; Investment</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/subastas" label="Subastas BOE" />
            <NavLink to="/orientacion" label="Orientación" />
            <NavLink to="/consultoria" label="Consultoría" />
            <NavLink to="/informe-premium" label="Informe Premium" />
            <NavLink to="/gestion-postsubasta" label="Gestión Post-Subasta" />
            <NavLink to="/blog" label="Blog" />
            <NavLink to="/contacto" label="Contacto" />
          </nav>
          <a href={buildWaUrl(WA_MSG.generic)} target="_blank" rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); trackEvent(EVENTS.CTA_WHATSAPP, { source: "header_desktop" }); openWhatsApp(WA_MSG.generic); }}
            className="hidden lg:inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold rounded-sm transition-colors"
            data-testid="site-header-whatsapp">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="md:hidden p-2 -mr-2 text-white hover:text-[#E0651F] transition-colors"
            aria-label="Abrir menú"
            data-testid="site-header-mobile-menu-btn"
          >
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </header>
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function MobileMenu({ open, onClose }) {
  return (
    <>
      <div
        className={`md:hidden fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`md:hidden fixed top-0 right-0 z-[70] h-full w-[85%] max-w-sm bg-[#2d1f1a] border-l border-[#3d2820] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="site-header-mobile-menu"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 h-20 border-b border-[#3d2820] flex-shrink-0">
          <Link to="/" onClick={onClose} className="flex items-center gap-2.5">
            <img src={ICON_FM} alt="" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black text-base tracking-tight text-white">FERNANDO MARCOS</div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-[#E0651F] font-bold">Subastas &amp; Investment</div>
            </div>
          </Link>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-stone-300 hover:text-white"
            aria-label="Cerrar menú"
            data-testid="site-header-mobile-menu-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          <MobileLink to="/subastas" label="Subastas BOE" featured />
          <MobileLink to="/orientacion" label="Orientación" />
          <MobileLink to="/consultoria" label="Consultoría" />
          <MobileLink to="/informe-premium" label="Informe Premium" />
          <MobileLink to="/gestion-postsubasta" label="Gestión Post-Subasta" />
          <MobileLink to="/blog" label="Blog · Guías BOE" />
          <MobileLink to="/contacto" label="Contacto" />
        </nav>
        <div className="px-6 py-5 border-t border-[#3d2820] space-y-3 flex-shrink-0">
          <a
            href={buildWaUrl(WA_MSG.generic)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); trackEvent(EVENTS.CTA_WHATSAPP, { source: "header_mobile_menu" }); openWhatsApp(WA_MSG.generic); }}
            className="flex items-center justify-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white py-3 text-[11px] tracking-[0.2em] uppercase font-bold rounded-sm transition-colors w-full"
            data-testid="site-header-mobile-whatsapp"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp directo
          </a>
          <div className="flex justify-center gap-5 pt-1">
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-[#E0651F]" aria-label="Instagram"><Instagram className="w-5 h-5" /></a>
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-[#E0651F]" aria-label="Facebook"><Facebook className="w-5 h-5" /></a>
            <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-[#E0651F]" aria-label="TikTok"><TikTokIcon className="w-5 h-5" /></a>
          </div>
        </div>
      </aside>
    </>
  );
}

function MobileLink({ to, label, featured }) {
  return (
    <Link
      to={to}
      className={`block px-4 py-3.5 rounded-sm text-sm font-bold tracking-wide transition-colors ${featured ? "text-[#E0651F] hover:bg-[#E0651F]/10" : "text-stone-200 hover:bg-stone-800 hover:text-white"}`}
      data-testid={`site-header-mobile-link-${to.replace(/\//g, "")}`}
    >
      {label}
    </Link>
  );
}

function NavLink({ to, label }) {
  return (
    <Link to={to} className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-stone-300 hover:text-[#E0651F] transition-colors">
      {label}
    </Link>
  );
}

export function SiteFooter() {
  return (
    <footer className="bg-[#1a110e] text-stone-400 pt-16 pb-8 px-6" data-testid="site-footer">
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10 mb-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={ICON_FM} alt="FM" className="h-10 w-10" />
            <div className="leading-none">
              <div className="font-display font-black text-base tracking-tight text-white">FERNANDO MARCOS</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E0651F] font-bold">Subastas & Investment</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed max-w-md">
            Asesoría profesional en subastas inmobiliarias del BOE. Te acompañamos desde la primera duda hasta la inscripción registral del inmueble adjudicado.
          </p>
          <p className="text-xs mt-4 text-stone-500">+12 años de experiencia. Abogados, procuradores y especialistas fiscales.</p>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-4">Servicios</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/subastas" className="hover:text-white" data-testid="footer-link-subastas">Subastas BOE · Buscador</Link></li>
            <li><Link to="/orientacion" className="hover:text-white">Orientación · 15 €</Link></li>
            <li><Link to="/consultoria" className="hover:text-white">Consultoría de Deudas · 95 €</Link></li>
            <li><Link to="/informe-premium" className="hover:text-white">Informe Premium · desde 135 €</Link></li>
            <li><Link to="/gestion-postsubasta" className="hover:text-white">Gestión Post-Subasta</Link></li>
            <li><Link to="/blog" className="hover:text-white" data-testid="footer-link-blog">Blog · Guías BOE</Link></li>
            <li><Link to="/contacto" className="hover:text-white">Contacto</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-4">Contacto</div>
          <ul className="space-y-2 text-sm">
            <li><a href="tel:+34695536486" className="hover:text-white inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> +34 695 536 486</a></li>
            <li><a href={buildWaUrl(WA_MSG.generic)} target="_blank" rel="noopener noreferrer"
              onClick={(e) => { e.preventDefault(); openWhatsApp(WA_MSG.generic); }}
              className="hover:text-white inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a></li>
            <li><a href="mailto:fmsubastasinvestment@gmail.com" className="hover:text-white inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</a></li>
          </ul>
          <div className="mt-5">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-2.5">Síguenos</div>
            <div className="flex items-center gap-2">
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                data-testid="footer-social-instagram"
                className="w-9 h-9 inline-flex items-center justify-center bg-stone-800 hover:bg-[#E0651F] hover:text-white text-stone-300 rounded-sm transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                data-testid="footer-social-facebook"
                className="w-9 h-9 inline-flex items-center justify-center bg-stone-800 hover:bg-[#E0651F] hover:text-white text-stone-300 rounded-sm transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                data-testid="footer-social-tiktok"
                className="w-9 h-9 inline-flex items-center justify-center bg-stone-800 hover:bg-[#E0651F] hover:text-white text-stone-300 rounded-sm transition-colors">
                <TikTokIcon className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto pt-6 border-t border-stone-800 text-[10px] tracking-[0.2em] uppercase text-stone-500 flex flex-wrap justify-between gap-3">
        <span>© {new Date().getFullYear()} Fernando Marcos Subastas &amp; Investment · NIF 45.091.305-N</span>
        <div className="flex flex-wrap gap-4">
          <Link to="/aviso-legal" className="hover:text-white" data-testid="footer-legal">Aviso Legal</Link>
          <Link to="/politica-privacidad" className="hover:text-white" data-testid="footer-privacy">Privacidad</Link>
          <Link to="/politica-cookies" className="hover:text-white" data-testid="footer-cookies">Cookies</Link>
          <Link to="/condiciones-contratacion" className="hover:text-white" data-testid="footer-terms">Condiciones</Link>
        </div>
        <span>Pagos seguros con Stripe</span>
      </div>
    </footer>
  );
}
